import { Message } from '@alifd/next';
import { Box } from '@mui/material';
import { DatePicker, Modal, Select } from 'antd';
import locale from 'antd/lib/date-picker/locale/zh_TW';
import axios from 'axios';
import moment, { Moment } from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import type { ResponseData as ChartMqttData } from '@/types/api/chargingPile/chargingPileMqttDataDailyDate';
import type {
  GetChargingPileHistoryDataInput as GetChartApiInputType,
  GetChargingPileHistoryDataOutput as GetChartApiOutputType,
} from '@/types/api/chargingPile/getChargingPileHistoryData';

import { GroupContext } from '../../../PileCtxProvider';
import Chart from './Chart';

const ChartDialog: React.FC = () => {
  const { chart: currentChart, setChart } = useContext(GroupContext);

  const [chartValueType, setChartValueType] = useState<'energy' | 'expense'>(
    'expense'
  );
  const [chartDateType, setChartDateType] = useState<
    'day' | 'week' | 'month' | 'year' | 'total'
  >('week');
  const [chosenTime, setChosenTime] = useState<Moment | null>(null);

  const [responseData, setResponseData] =
    useState<GetChartApiOutputType | null>(null);

  const [availableTime, setAvailableTime] = useState<string[]>([]);

  useEffect(() => {
    if (currentChart && currentChart.show) {
      (async function getAvailableTime() {
        try {
          const res = await axios.get<ChartMqttData>(
            '/ezcon/api/chargingPileMqttDataDailyDate',
            {
              params: {
                id: currentChart.id,
                type: currentChart.type,
              },
              headers: {
                authorization: localStorage.getItem('feathers-jwt'),
              },
            }
          );
          setAvailableTime(res.data);
          setChosenTime(() => {
            return moment(res.data.at(-1));
          });
        } catch (err) {
          console.error(err);
          Message.error('取得可用時間失敗');
        }
      })();
    }
  }, [currentChart]);

  const getChartData = useCallback(
    async (time: Moment, chartType: typeof chartDateType) => {
      if (currentChart && currentChart.show) {
        const params: GetChartApiInputType = {
          id: currentChart.id,
          type: currentChart.type,
          timeStamp: time.valueOf(),
          chartType,
        };
        try {
          const { data: resData } = await axios.get<GetChartApiOutputType>(
            '/ezcon/api/getChargingPileHistoryData',
            {
              params,
              headers: {
                authorization: localStorage.getItem('feathers-jwt'),
              },
            }
          );
          setResponseData(resData);
        } catch (err) {
          console.error(err);
          Message.error('取得資料失敗');
        }
      }
    },
    [currentChart]
  );

  useEffect(() => {
    if (chosenTime) {
      getChartData(chosenTime, chartDateType);
    }
  }, [chartDateType, getChartData, chosenTime]);

  const handleCancel = () => {
    setChart({
      id: '',
      type: '',
      name: '',
      show: false,
    });
    setResponseData(null);
    setChartValueType('expense');
  };

  const chartTypeOptions = [
    {
      label: '天',
      value: 'day',
    },
    {
      label: '週',
      value: 'week',
    },
    {
      label: '月',
      value: 'month',
    },
    {
      label: '年',
      value: 'year',
    },
    {
      label: '全部',
      value: 'total',
    },
  ];

  return (
    <Modal
      title={<h2>{`${currentChart?.name || ''}用電圖表`}</h2>}
      {...{
        open: currentChart?.show || false,
        footer: false,
        onCancel: handleCancel,
        width: 'fit-content',
      }}
    >
      <Box
        id="chart-container"
        sx={{
          width: 700,
          height: 600,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        {responseData !== null && (
          <>
            <Chart {...{ responseData, chartValueType, chartDateType }} />
            <Box
              id="chart-controller"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                width: 700,
              }}
            >
              <Select
                options={[
                  {
                    label: '費用',
                    value: 'expense',
                  },
                  {
                    label: '消耗電量',
                    value: 'energy',
                  },
                ]}
                style={{ width: 100 }}
                value={chartValueType}
                onChange={(value) => setChartValueType(value)}
              />
              <Select
                options={chartTypeOptions}
                onSelect={(
                  value: 'day' | 'week' | 'month' | 'year' | 'total'
                ) => {
                  setChartDateType(value);
                }}
                style={{ width: 100 }}
                value={chartDateType}
                allowClear={false}
              />
              <DatePicker
                style={{ width: '200px' }}
                locale={locale}
                disabled={chartDateType === 'total'}
                disabledDate={(date) =>
                  !availableTime.includes(date.format('YYYY-MM-DD'))
                }
                picker={
                  ['day', 'total'].includes(chartDateType)
                    ? { day: 'date', total: 'year' }[chartDateType]
                    : chartDateType
                }
                onChange={(date) => {
                  setChosenTime((prev) => {
                    if (
                      date &&
                      availableTime.some((time) =>
                        date.isSame(
                          time,
                          chartDateType === 'total' ? 'year' : chartDateType
                        )
                      )
                    ) {
                      return date;
                    }
                    return prev;
                  });
                }}
                value={moment(chosenTime)}
                format={(data: moment.Moment) => {
                  let formatText: string;
                  switch (chartDateType) {
                    default:
                      formatText = 'M/D';
                      break;
                    case 'day':
                      formatText = 'M月D日';
                      break;
                    case 'week':
                      formatText = 'M月D日';
                      break;
                    case 'month':
                      formatText = 'M月';
                      break;
                    case 'year':
                      formatText = 'YYYY年';
                      break;
                    case 'total':
                      formatText = 'YYYY年';
                      break;
                  }
                  if (chartDateType !== 'week') {
                    return data.format(formatText);
                  } else {
                    return `${data.startOf('week').format(formatText)}-${data
                      .endOf('week')
                      .format(formatText)}`;
                  }
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ChartDialog;
